/**
 * AI Brand Profile React Query Hooks
 *
 * Custom hooks for managing AI brand profiles state and mutations
 */

import { useCallback } from 'react';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { useGatingStore } from '@/stores/gating.store';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from '@/utils/toast';
import { isGlobalGatingHandledError } from '@/services/api';
import { aiBrandProfileService, BRAND_PROFILE_SYSTEM_MAX } from '@/services/ai-brand-profile.service';
import type {
  CreateAIBrandProfileInput,
  UpdateAIBrandProfileInput,
  AIBrandProfileDraft,
  SaveAIBrandProfileDraftInput,
  AIBrandProfileVersion,
} from '@/types/ai';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';

// ===========================
// Query Hooks
// ===========================

/**
 * Hook to fetch all brand profiles
 * Uses team-scoped query keys for proper cache isolation in God Mode
 */
export const useAIBrandProfiles = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  const query = useQuery({
    queryKey: teamQueryKeys.ai.brandProfiles(teamId),
    queryFn: aiBrandProfileService.getProfiles,
    enabled: hasTeam,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Computed: default profile — the server rejects setting a DRAFT as default, but
  // guard here too so generation flows relying on `defaultProfile` never pick one up.
  const defaultProfile = query.data?.profiles.find(profile => profile.isDefault && profile.status === 'ACTIVE') || null;

  return {
    profiles: query.data?.profiles ?? [],
    brandProfileLimit: query.data?.brandProfileLimit ?? BRAND_PROFILE_SYSTEM_MAX,
    defaultProfile,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Create-door guard shared by every "new Brand World" entry point. The count comes from the same list the
 * backend counts (all statuses, drafts included). `atPackageLimit` = full because of the PACKAGE limit
 * (below the system cap): the door should open the upsell card instead of the wizard.
 */
export type BrandProfileGateState = 'ok' | 'upsell' | 'systemCap';

export const useBrandProfileCreateGate = () => {
  const { profiles, brandProfileLimit } = useAIBrandProfiles();
  const brandWorldLock = useFeatureLock('brand_world');
  const openUpsell = useGatingStore((s) => s.openUpsell);
  const maxProfiles = Math.min(BRAND_PROFILE_SYSTEM_MAX, brandProfileLimit);
  // ok = may create; upsell = full because of the PACKAGE limit (< system cap); systemCap = 10 reached
  const state: BrandProfileGateState =
    profiles.length >= BRAND_PROFILE_SYSTEM_MAX ? 'systemCap' : profiles.length >= maxProfiles ? 'upsell' : 'ok';
  const canCreateMore = state === 'ok';
  const atPackageLimit = state === 'upsell';
  const atSystemCap = state === 'systemCap';
  const showLimitUpsell = useCallback(() => {
    openUpsell(
      {
        feature: 'quota:brand_profiles',
        variant: 'quota',
        upsellPlanSlug: brandWorldLock.upsellPlanSlug,
        quota: { quotaKey: 'brand_profiles', used: profiles.length, limit: maxProfiles, resetsAt: null, fixedLimit: true },
      },
      true
    );
  }, [openUpsell, brandWorldLock.upsellPlanSlug, profiles.length, maxProfiles]);
  return { state, maxProfiles, canCreateMore, atPackageLimit, atSystemCap, showLimitUpsell };
};

/**
 * Hook to fetch single brand profile by ID
 */
export const useAIBrandProfile = (id: number | null) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: [...teamQueryKeys.ai.brandProfiles(teamId), id],
    queryFn: () => aiBrandProfileService.getProfile(id!),
    enabled: hasTeam && id !== null,
    staleTime: 5 * 60 * 1000,
  });
};

// ===========================
// Mutation Hooks
// ===========================

/**
 * Hook to create brand profile
 */
export const useCreateAIBrandProfile = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (input: CreateAIBrandProfileInput) =>
      aiBrandProfileService.createProfile(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      // Backend deletes the server-side wizard draft when a profile is created —
      // invalidate the cached draft so any "resume draft" UI disappears immediately.
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfileDraft(teamId) });
      toast.success('Brand World created successfully');
    },
    onError: (error: AxiosError<{ error?: string; message?: string; details?: { issues?: Array<{ path?: string[]; message?: string }> } }>) => {
      // Package limit reached: the global upsell card (opened by the axios interceptor) explains it
      if (isGlobalGatingHandledError(error)) return;
      const data = error.response?.data;
      // Check for validation errors with details
      if (data?.details?.issues?.length) {
        const issue = data.details.issues[0];
        const field = issue.path?.[0] || 'Field';
        toast.error(`${field}: ${issue.message}`);
      } else {
        toast.error(data?.error || data?.message || 'Failed to create Brand World');
      }
    },
  });
};

/**
 * Hook to update brand profile
 */
export const useUpdateAIBrandProfile = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateAIBrandProfileInput }) =>
      aiBrandProfileService.updateProfile(id, input),
    onSuccess: (_profile, { id }) => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      // Every update creates a new version server-side — invalidate the version
      // history too, so it's fresh if the user opens it immediately after editing.
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.ai.brandProfiles(teamId), id, 'versions'] });
      toast.success('Brand World updated successfully');
    },
    onError: (error: AxiosError<{ error?: string; message?: string; details?: { issues?: Array<{ path?: string[]; message?: string }> } }>) => {
      if (isGlobalGatingHandledError(error)) return;
      const data = error.response?.data;
      if (data?.details?.issues?.length) {
        const issue = data.details.issues[0];
        const field = issue.path?.[0] || 'Field';
        toast.error(`${field}: ${issue.message}`);
      } else {
        toast.error(data?.error || data?.message || 'Failed to update Brand World');
      }
    },
  });
};

/**
 * Hook to delete brand profile
 */
export const useDeleteAIBrandProfile = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => aiBrandProfileService.deleteProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      toast.success('Brand World deleted successfully');
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      if (isGlobalGatingHandledError(error)) return;
      const data = error.response?.data;
      toast.error(data?.error || data?.message || 'Failed to delete Brand World');
    },
  });
};

/**
 * Hook to set profile as default
 */
export const useSetDefaultAIBrandProfile = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => aiBrandProfileService.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      toast.success('Default Brand World updated');
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      if (isGlobalGatingHandledError(error)) return;
      const data = error.response?.data;
      toast.error(data?.error || data?.message || 'Failed to set default Brand World');
    },
  });
};

/**
 * Hook to activate a DRAFT profile
 */
export const useActivateAIBrandProfile = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => aiBrandProfileService.activateProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      toast.success('Brand World activated');
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      if (isGlobalGatingHandledError(error)) return;
      const data = error.response?.data;
      toast.error(data?.error || data?.message || 'Failed to activate Brand World');
    },
  });
};

/**
 * Hook to duplicate a profile into a new DRAFT copy
 */
export const useDuplicateAIBrandProfile = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (id: number) => aiBrandProfileService.duplicateProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      toast.success('Draft copy created');
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      // Package limit reached: the global upsell card already explains it
      if (isGlobalGatingHandledError(error)) return;
      const data = error.response?.data;
      toast.error(data?.error || data?.message || 'Failed to duplicate Brand World');
    },
  });
};

/**
 * Hook to fetch a profile's version history, newest first.
 */
export const useAIBrandProfileVersions = (id: number | null) => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery<AIBrandProfileVersion[]>({
    queryKey: [...teamQueryKeys.ai.brandProfiles(teamId), id, 'versions'],
    queryFn: () => aiBrandProfileService.getVersions(id!),
    enabled: hasTeam && id !== null,
    staleTime: 30 * 1000,
  });
};

/**
 * Hook to restore a profile to a prior version.
 */
export const useRestoreAIBrandProfileVersion = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: ({ id, versionNumber }: { id: number; versionNumber: number }) =>
      aiBrandProfileService.restoreVersion(id, versionNumber),
    onSuccess: (_profile, { id }) => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.ai.brandProfiles(teamId) });
      queryClient.invalidateQueries({ queryKey: [...teamQueryKeys.ai.brandProfiles(teamId), id, 'versions'] });
      toast.success('Brand World restored');
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      if (isGlobalGatingHandledError(error)) return;
      const data = error.response?.data;
      toast.error(data?.error || data?.message || 'Failed to restore version');
    },
  });
};

// ===========================
// Server-side Draft Hooks (Create Wizard resume-across-devices)
// ===========================

/**
 * Hook to fetch the server-side draft for the "Create AI Brand Profile" wizard.
 * Returns `null` when there is no saved draft for the team.
 *
 * Shared/cached by team so both the wizard (restore-on-open) and any
 * "Resume draft" entry points (e.g. AIBrandProfileSettings) stay in sync.
 */
export const useBrandProfileDraft = () => {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery<AIBrandProfileDraft | null>({
    queryKey: teamQueryKeys.ai.brandProfileDraft(teamId),
    queryFn: aiBrandProfileService.getDraft,
    enabled: hasTeam,
    staleTime: 30 * 1000, // 30s — cheap to refetch, but avoid hammering on every mount
  });
};

/**
 * Hook to save (upsert) the wizard draft to the server.
 * Intentionally silent (no toast) — autosave should be unobtrusive; the wizard
 * shows its own inline "Saving…" / "Draft saved" indicator instead.
 */
export const useSaveBrandProfileDraft = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (input: SaveAIBrandProfileDraftInput) => aiBrandProfileService.saveDraft(input),
    onSuccess: (draft) => {
      queryClient.setQueryData(teamQueryKeys.ai.brandProfileDraft(teamId), draft);
    },
  });
};

/**
 * Hook to discard the server-side wizard draft.
 */
export const useDeleteBrandProfileDraft = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: () => aiBrandProfileService.deleteDraft(),
    onSuccess: () => {
      queryClient.setQueryData(teamQueryKeys.ai.brandProfileDraft(teamId), null);
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      if (isGlobalGatingHandledError(error)) return;
      const data = error.response?.data;
      toast.error(data?.error || data?.message || 'Failed to discard draft');
    },
  });
};
